import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Icon from '../docs/Icon'
import Editable from '../inspector/Editable'
import Banner from '../site/Banner'
import { usePrefs } from '@/state/prefs'
import { textSizeGroup,
  accordionIconOptions,
  accordionOptions,
  alertOptions,
  bannerGroups,
  sectionLinkOptions,
  buttonOptions,
  fieldOptions,
  labelOptions,
  densityOptions,
  disclosureOptions,
  displayFontOptions,
  eyebrowOptions,
  fontDetail,
  radiusOptions,
  sansFontOptions,
} from './options'
import { sansFonts } from '@/content/fonts'
import Cased from '../ui/Cased'
import DatePicker from '../library/ui/DatePicker'
import TimePicker from '../library/ui/TimePicker'
import type { DateFormat, HintPlacement } from '../library/ui/dates'
import DataTable from '../library/ui/DataTable'
import { officeColumns, offices } from '../library/examples'

const placementOptions = [
  { value: 'inline', label: 'Inline' },
  { value: 'below', label: 'Below' },
  { value: 'full', label: 'Full width' },
]

const sampleOptions = [
  { value: 'empty', label: 'Empty' },
  { value: 'placeholder', label: 'Placeholder' },
  { value: 'filled', label: 'Filled' },
]

/** A ZIP code field whose label, input and button each carry a design note. */
function FieldDemo() {
  const { prefs, set } = usePrefs()
  const [value, setValue] = useState('')
  const [touched, setTouched] = useState(false)
  const [placement, setPlacement] = useState('inline')
  const [sample, setSample] = useState('empty')
  const showSample = (next: string) => {
    setSample(next)
    setValue(next === 'filled' ? '20500' : '')
    setTouched(false)
  }
  const invalid = touched && !/^\d{5}$/.test(value)
  return (
    <form
      className="mds-field"
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        setTouched(true)
      }}
    >
      <Editable
        label="Label"
        value={prefs.label}
        options={labelOptions}
        onChange={(next) => set('label', next)}
        token="data-label"
        pin="right"
        as="span"
        className="bento-field__label"
      >
        <label className="mds-label" htmlFor="bento-zip">
          ZIP code
        </label>
      </Editable>
      <span className="mds-hint" id="bento-zip-hint">
        Five digits
      </span>
      <div className={`mds-cluster bento-field__row bento-field__row--${placement}`}>
        <Editable
          label="Text field"
          value={prefs.field}
          options={fieldOptions}
          onChange={(next) => set('field', next)}
          more={[{ label: 'Sample', value: sample, options: sampleOptions, onChange: showSample }]}
          token="data-field"
          detail='autocomplete="postal-code" lets browsers fill the ZIP code (WCAG 1.3.5).'
          pin="below-left"
          className="bento-field__input"
        >
          <input
            id="bento-zip"
            className="mds-input"
            inputMode="numeric"
            autoComplete="postal-code"
            placeholder={sample === 'placeholder' ? 'e.g. 20500' : undefined}
            value={value}
            aria-invalid={invalid}
            aria-describedby={`bento-zip-hint${invalid ? ' bento-zip-error' : ''}`}
            onChange={(event) => setValue(event.target.value)}
            onBlur={() => setTouched(true)}
          />
        </Editable>
        <Editable
          label="Field button"
          optionsLabel="Placement"
          value={placement}
          options={placementOptions}
          onChange={setPlacement}
          token="button placement"
          pin={placement === 'below' ? 'below-left' : 'below'}
          as="span"
          className="bento-field__button"
        >
          <button className="mds-button mds-button--primary">Check</button>
        </Editable>
      </div>
      {invalid && (
        <span className="mds-error-text" id="bento-zip-error">
          Enter a five-digit ZIP code.
        </span>
      )}
      {touched && !invalid && (
        <span className="mds-hint" role="status">
          Looks good.
        </span>
      )}
    </form>
  )
}

const onOff = [
  { value: 'on', label: 'On' },
  { value: 'off', label: 'Off' },
]

const dateFormatOptions = [
  { value: 'mdy', label: 'MM/DD/YYYY' },
  { value: 'iso', label: 'YYYY-MM-DD' },
]
const clockOptions = [
  { value: '12', label: '12-hour' },
  { value: '24', label: '24-hour' },
]
const hintOptions = [
  { value: 'above', label: 'Above' },
  { value: 'placeholder', label: 'Placeholder' },
  { value: 'both', label: 'Both' },
]

/**
 * A date and time tile, a pill on each field. Typing always works, so the
 * calendar can be switched off; the format hint can sit above the field, in
 * it, or both.
 */
function DateTimeTile() {
  const [calendar, setCalendar] = useState('on')
  const [format, setFormat] = useState<DateFormat>('mdy')
  const [dateHint, setDateHint] = useState<HintPlacement>('above')
  const [clock, setClock] = useState('12')
  const [zone, setZone] = useState('off')
  const [timeHint, setTimeHint] = useState<HintPlacement>('above')
  return (
    <div className="bento__tile bento__tile--date">
      <span className="mds-eyebrow">Date and time</span>
      <div className="bento-date">
        <Editable
          label="Date"
          optionsLabel="Calendar"
          value={calendar}
          options={onOff}
          onChange={setCalendar}
          more={[
            { label: 'Format', value: format, options: dateFormatOptions, onChange: (value) => setFormat(value as DateFormat) },
            { label: 'Format hint', value: dateHint, options: hintOptions, onChange: (value) => setDateHint(value as HintPlacement) },
          ]}
          token="DatePicker"
          detail="Typing always works; the calendar is an addition, never the only way in. A hint above the field stays visible while typing."
          pin="right"
        >
          {/* Keyed on the format, so a switch starts the field empty rather than misreading it. */}
          <DatePicker key={format} label="Date" calendar={calendar === 'on'} format={format} hintPlacement={dateHint} id="bento-date" />
        </Editable>
        <Editable
          label="Time"
          optionsLabel="Clock"
          value={clock}
          options={clockOptions}
          onChange={setClock}
          more={[
            { label: 'Time zone', value: zone, options: onOff, onChange: setZone },
            { label: 'Format hint', value: timeHint, options: hintOptions, onChange: (value) => setTimeHint(value as HintPlacement) },
          ]}
          token="TimePicker"
          detail="A combo box of times at a set step; type to filter, or open the list with the clock."
          pin="right"
        >
          <TimePicker
            key={clock}
            label="Time"
            min={8 * 60}
            max={17 * 60}
            clock={clock === '24' ? 24 : 12}
            timeZone={zone === 'on'}
            hintPlacement={timeHint}
            id="bento-time"
          />
        </Editable>
      </div>
    </div>
  )
}

const headerOptions = [
  { value: 'plain', label: 'Plain' },
  { value: 'tinted', label: 'Tinted' },
  { value: 'solid', label: 'Solid' },
]

/** A data table tile: striping and row selection on the table, sorting and style on its header row. */
function DataTableTile() {
  const [striped, setStriped] = useState('on')
  const [select, setSelect] = useState('off')
  const [sort, setSort] = useState('on')
  const [header, setHeader] = useState<'plain' | 'tinted' | 'solid'>('plain')
  const [bold, setBold] = useState('off')
  const [underline, setUnderline] = useState('off')
  return (
    <Editable
      label="Data table"
      optionsLabel="Striped"
      value={striped}
      options={onOff}
      onChange={setStriped}
      more={[{ label: 'Row select', value: select, options: onOff, onChange: setSelect }]}
      token="mds-table"
      className="bento__tile bento__tile--table"
    >
      <span className="mds-eyebrow">Data table</span>
      <Editable
        label="Header row"
        optionsLabel="Style"
        value={header}
        options={headerOptions}
        onChange={(value) => setHeader(value as typeof header)}
        more={[
          { label: 'Sorting', value: sort, options: onOff, onChange: setSort },
          { label: 'Bold', value: bold, options: onOff, onChange: setBold },
          { label: 'Underline', value: underline, options: onOff, onChange: setUnderline },
        ]}
        token="mds-table--header"
        detail="Sortable columns are buttons that announce their order with aria-sort."
        className="bento-table__head"
      >
        {/* Keyed on sorting, so turning it off also drops any sort in place. */}
        <DataTable
          key={sort}
          caption="Field offices"
          rowKey="id"
          columns={sort === 'on' ? officeColumns : officeColumns.map((column) => ({ ...column, sortable: false }))}
          rows={offices.slice(0, 4)}
          striped={striped === 'on'}
          selectable={select === 'on'}
          header={header}
          headerBold={bold === 'on'}
          headerUnderline={underline === 'on'}
        />
      </Editable>
    </Editable>
  )
}

export default function ComponentsBento() {
  const { prefs, set } = usePrefs()
  const navigate = useNavigate()
  return (
    <section className="mds-section home-bento" aria-labelledby="bento-title">
      <div className="mds-container">
        <div className="mds-section-head">
          <Editable
            label="Eyebrow"
            value={prefs.eyebrow}
            options={eyebrowOptions}
            onChange={(value) => set('eyebrow', value)}
            token="data-eyebrow"
            pin="right"
            as="span"
            className="home-bento__eyebrow"
          >
            <span className="mds-eyebrow">Components</span>
          </Editable>
          <Editable
            label="Headline font"
            value={prefs.fontDisplay}
            options={displayFontOptions}
            onChange={(value) => set('fontDisplay', value)}
            token="--mds-font-display"
            detail={fontDetail(prefs.fontDisplay)}
            pin="right"
            as="span"
            className="home-bento__title"
          >
            <h2 id="bento-title" className="mds-display">
              <Cased>Ready for every mission.</Cased>
            </h2>
          </Editable>
          <Editable
            label="Body font"
            value={prefs.fontSans}
            options={sansFontOptions}
            onChange={(value) => set('fontSans', value)}
            token="--mds-font-sans"
            optionsLabel="Font"
            more={[textSizeGroup(prefs.textSize, (value) => set('textSize', value))]}
            detail={fontDetail(prefs.fontSans, sansFonts)}
            pin="right"
            className="home-bento__lead"
          >
            <p className="mds-lead">
              Accessible components with light and dark themes, a skin for every agency, and a clear migration path for
              existing federal sites. Every tile below is the real component.
            </p>
          </Editable>
        </div>
        <div className="bento">
          <Editable
            label="Corner shape"
            value={prefs.radius}
            options={radiusOptions}
            onChange={(value) => set('radius', value)}
            token="--mds-radius-control"
            className="bento__tile bento__tile--buttons"
          >
            <span className="mds-eyebrow">Button</span>
            <Editable
              label="Button font"
              optionsLabel="Text"
              value={prefs.button}
              options={buttonOptions}
              onChange={(value) => set('button', value)}
              token="data-button"
              pin="right"
              className="bento-buttons"
            >
              <div className="mds-cluster">
                <button type="button" className="mds-button mds-button--primary">Primary</button>
                <button type="button" className="mds-button mds-button--secondary">Secondary</button>
                <button type="button" className="mds-button mds-button--accent">Accent</button>
                <button type="button" className="mds-button mds-button--ghost">Ghost</button>
              </div>
            </Editable>
          </Editable>
          <Editable
            label="Banner"
            optionsLabel="Disclosure"
            value={prefs.bannerMode}
            options={disclosureOptions}
            onChange={(value) => set('bannerMode', value as typeof prefs.bannerMode)}
            more={bannerGroups(prefs, set)}
            token="banner"
            className="bento__tile bento__tile--banner"
          >
            <span className="mds-eyebrow">Banner</span>
            <div className="bento-banner">
              <Banner specimen onNavigate={(href) => navigate(href)} />
            </div>
          </Editable>
          <Editable
            label="Density"
            value={prefs.density}
            options={densityOptions}
            onChange={(value) => set('density', value)}
            token="--mds-density"
            className="bento__tile bento__tile--field"
          >
            <span className="mds-eyebrow">Form field</span>
            <FieldDemo />
          </Editable>
          <Editable
            label="Alert"
            value={prefs.alert}
            options={alertOptions}
            onChange={(value) => set('alert', value)}
            token="data-alert"
            className="bento__tile bento__tile--alert"
          >
            <span className="mds-eyebrow">Alert</span>
            <div className="mds-alert mds-alert--success" role="status">
              <Icon name="check" className="mds-alert__icon" />
              <div>
                <p className="mds-alert__title">Application received</p>
                <p className="mds-muted">We sent a confirmation to your email.</p>
              </div>
            </div>
            <div className="mds-alert mds-alert--warning">
              <Icon name="info" className="mds-alert__icon" />
              <div>
                <p className="mds-alert__title">Action needed by June 30</p>
              </div>
            </div>
          </Editable>
          <DateTimeTile />
          <Editable
            label="Accordion"
            optionsLabel="Style"
            value={prefs.accordion}
            options={accordionOptions}
            onChange={(value) => set('accordion', value)}
            more={[
              {
                label: 'Icon',
                value: prefs.accordionIcon,
                options: accordionIconOptions,
                onChange: (value) => set('accordionIcon', value as typeof prefs.accordionIcon),
              },
            ]}
            token="data-accordion"
            className="bento__tile bento__tile--accordion"
          >
            <span className="mds-eyebrow">Accordion</span>
            <div className="mds-accordion">
              {[
                ['Who can apply?', 'Any U.S. citizen with a valid passport issued in the last 15 years.'],
                ['How long does it take?', 'Routine service takes four to six weeks from the day we receive your application.'],
              ].map(([question, answer]) => (
                <details key={question} className="mds-accordion__item">
                  <summary className="mds-accordion__summary">
                    {question}
                    <span className="mds-accordion__icon" aria-hidden="true" />
                  </summary>
                  <p className="mds-accordion__body">{answer}</p>
                </details>
              ))}
            </div>
          </Editable>
          <DataTableTile />
        </div>
        <p className="home-bento__more">
          <Editable
            label="Section link"
            value={prefs.sectionLink}
            options={sectionLinkOptions}
            onChange={(value) => set('sectionLink', value)}
            token="mds-button"
            detail="The link that closes a section and leads to more of it."
            pin="right"
            as="span"
          >
            <Link
              to="/components"
              className={`mds-button ${{ outline: 'mds-button--secondary', solid: 'mds-button--primary', text: 'mds-button--ghost' }[prefs.sectionLink]}`}
            >
              <Cased>Browse all components</Cased> <Icon name="arrow" size={16} />
            </Link>
          </Editable>
        </p>
      </div>
    </section>
  )
}
